import React, { useState } from 'react'
import { Modal, ModalBody, Row, Col, Button, FormGroup, Label, Input } from 'reactstrap'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { addCard } from '../../helper/backend-methods'
import { showToast } from '../../components/Toast'

const cardElementOptions = {
    style: {
        base: {
            fontSize: '15px',
            color: '#212529',
            '::placeholder': { color: '#6c757d' },
        },
        invalid: { color: '#dc3545' },
    },
};

const AddCardModal = ({ isOpen, toggle, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardholderName, setCardholderName] = useState("");
    const [isPrimary, setIsPrimary] = useState(true);
    const [cardComplete, setCardComplete] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleClose = () => {
        if (submitting) return;
        setCardholderName("");
        setIsPrimary(true);
        setCardComplete(false);
        toggle();
    };

    const handleSubmit = async () => {
        if (!stripe || !elements) return;

        const card = elements.getElement(CardElement);
        if (!card) {
            showToast("error", "Payment Error", "Card input not available. Please refresh the page.");
            return;
        }

        if (!cardComplete) {
            showToast("error", "Incomplete Card Details", "Please enter your complete card number, expiry date, and CVC before submitting.");
            return;
        }

        setSubmitting(true);
        try {
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card,
                ...(cardholderName && { billing_details: { name: cardholderName } }),
            });

            if (error) {
                showToast("error", "Payment Failed", error.message || "We couldn't process your card. Please check your details and try again.");
                setSubmitting(false);
                return;
            }

            const { data } = await addCard({
                paymentMethodId: paymentMethod.id,
                isPrimary,
                card: Number(paymentMethod.card.last4),
                brand: paymentMethod.card.brand,
            });

            if (data?.success) {
                showToast("success", "Card Added", "Your card was added successfully.");
                setCardholderName("");
                setIsPrimary(true);
                setCardComplete(false);
                onSuccess();
            } else {
                showToast("error", "Request Failed", data?.message || "We couldn't add your card. Please try again.");
            }
        } catch (err) {
            showToast("error", "Payment Failed", err.response?.data?.message || "Something went wrong while adding your card. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered={true} size='md'>
            <ModalBody>
                <Row>
                    <Col md={12}>
                        <div className="text-center">
                            <h3 className="mt-2 mb-4 fs-26 text-dark">Add Card</h3>
                        </div>
                        <FormGroup>
                            <Label className="fw-bold mb-2 text-black fs-14">Cardholder name</Label>
                            <Input
                                type="text"
                                placeholder="Full name on card"
                                value={cardholderName}
                                onChange={(e) => setCardholderName(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label className="fw-bold mb-2 text-black fs-14">Card information</Label>
                            <div className="form-control" style={{ height: 'auto', padding: '10px 12px' }}>
                                <CardElement options={cardElementOptions} onChange={(e) => setCardComplete(e.complete)} />
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <Label className="fw-bold mb-2 text-black fs-14 d-block">Set as primary card?</Label>
                            <div className="custom-radio-group">
                                <div className="custom-radio-option">
                                    <Input
                                        type="radio"
                                        name="isPrimary"
                                        id="isPrimaryYes"
                                        checked={isPrimary}
                                        onChange={() => setIsPrimary(true)}
                                        className="custom-radio-input"
                                    />
                                    <Label for="isPrimaryYes" className="custom-radio-label">
                                        Yes
                                    </Label>
                                </div>
                                <div className="custom-radio-option">
                                    <Input
                                        type="radio"
                                        name="isPrimary"
                                        id="isPrimaryNo"
                                        checked={!isPrimary}
                                        onChange={() => setIsPrimary(false)}
                                        className="custom-radio-input"
                                    />
                                    <Label for="isPrimaryNo" className="custom-radio-label">
                                        No
                                    </Label>
                                </div>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="text-center mt-3">
                    <Col>
                        <button disabled={submitting} onClick={handleClose} className="px-2 py-2 rounded outline-gradient-btn w-100">
                            <span>Cancel</span>
                        </button>
                    </Col>
                    <Col>
                        <Button
                            className="font-normal py-2 px-3 rounded-2 fs-14 text-decoration-none text-white linear-gradient-btn w-100"
                            onClick={handleSubmit}
                            disabled={submitting || !stripe}
                        >
                            {submitting ? "Please Wait" : "Add Card"}
                        </Button>
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    );
};

export default AddCardModal;
